/**
 * TenderChatEmptyState — Shown when the user hasn't started a chat yet.
 *
 * Two modes:
 * - With documents: heading + one line; starters render separately above the
 *   input via TenderChatSuggestions.
 * - Without documents: upload CTA that is also a drop target — dropped files
 *   upload straight into this bid's tender pack, then navigate to Documents
 *   so the user sees upload progress.
 */

import { Upload, ListChecks, ClipboardList, Compass, FileCheck } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useDropzone } from "react-dropzone"
import { Button } from "@/components/ui/button"
import { StarterList, type Starter } from "@/components/chat/StarterList"
import { useDocumentsStore } from "@/stores/documents-store"
import { useBidsStore } from "@/stores/bids-store"
import { useTenderChatStore } from "@/stores/tender-chat-store"
import { getAcceptedFileTypes } from "@/lib/schemas"
import { cn } from "@/lib/utils"

interface TenderChatEmptyStateProps {
  bidId: string
}

const TENDER_STARTERS: Starter[] = [
  { text: 'What are the evaluation criteria and their weightings?', icon: ListChecks },
  { text: 'List every mandatory requirement we must meet', icon: ClipboardList },
  { text: 'What does the Bid Brief say we should lean on?', icon: Compass },
  { text: 'How is our draft tracking against the requirements?', icon: FileCheck },
]

/**
 * Starter rows rendered separately, above the chat input. Hidden until the bid
 * has documents — the starters all read the tender, so they are dead ends on an
 * empty bid where the empty state asks for an upload instead.
 */
export function TenderChatSuggestions() {
  const sendMessage = useTenderChatStore((s) => s.sendMessage)
  const documents = useDocumentsStore((s) => s.documents)

  if (documents.length === 0) return null

  return (
    <div className="flex justify-center px-4 pb-4">
      <StarterList starters={TENDER_STARTERS} onSelect={(text) => void sendMessage(text)} />
    </div>
  )
}

export function TenderChatEmptyState({ bidId }: TenderChatEmptyStateProps) {
  const navigate = useNavigate()
  const documents = useDocumentsStore((s) => s.documents)
  const uploadTenderDocuments = useDocumentsStore((s) => s.uploadTenderDocuments)
  const bid = useBidsStore((s) => s.bids.find((b) => b.id === bidId))

  const hasDocuments = documents.length > 0

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted: File[]) => {
      if (accepted.length === 0) return
      void uploadTenderDocuments(bidId, accepted)
      // The Documents tab shows the upload queue and progress.
      void navigate(`/app/bids/${bidId}/documents`)
    },
    accept: getAcceptedFileTypes(),
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: hasDocuments,
  })

  if (!hasDocuments) {
    return (
      <div
        {...getRootProps()}
        data-testid="tender-chat-drop-target"
        className={cn(
          "flex flex-col items-center justify-center text-center rounded-xl border-2 border-dashed px-10 py-8 transition-colors",
          isDragActive
            ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/5"
            : "border-transparent"
        )}
      >
        <input {...getInputProps()} aria-hidden="true" tabIndex={-1} />
        <Upload className="mx-auto mb-4 h-8 w-8 text-muted-foreground" />
        <h3 className="text-[28px] [@media(max-height:720px)]:text-2xl font-medium tracking-tight leading-tight mb-5 text-foreground">
          Upload tender documents to start
        </h3>
        <p className="text-base text-muted-foreground mb-5 max-w-sm">
          Drop files here, or add them from the Documents tab.
        </p>
        <Button variant="outline" onClick={() => void navigate(`/app/bids/${bidId}/documents`)}>
          Go to Documents
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center text-center">
      <h2 className="text-[28px] [@media(max-height:720px)]:text-2xl font-medium tracking-tight leading-tight mb-5 text-foreground">
        Ask this tender
      </h2>
      <p className="text-base text-muted-foreground max-w-sm">
        {bid?.title ?? 'Answers cite the tender documents for this bid.'}
      </p>
    </div>
  )
}
